import Box from '@mui/material/Box';
import type { SxProps, Theme } from '@mui/material/styles';

/**
 * Wraps a header cell's content with a fixed pixel width and a drag handle
 * in its trailing padding. The handle stays fully inside this column's own
 * box (never crosses into the next column) so it can't land on top of the
 * next column's label, which starts flush at its own left edge.
 * `onResizeStart` is bound to both mouse and touch start so drag-resize
 * works on touch devices too (pointerdown alone does not).
 * Omit `onResizeStart` to render the cell without a drag handle.
 */
export const ResizableHeaderCell = ({ width, onResizeStart, onResetSize, children, sx }: {
    /** Pixels or any CSS width (e.g. a `var(...)`). Applied inline so changes don't generate new style classes. */
    width: number | string;
    onResizeStart?: (event: React.MouseEvent | React.TouchEvent) => void;
    onResetSize?: () => void;
    children: React.ReactNode;
    sx?: SxProps<Theme>;
}) => (
    <Box style={{ width }} sx={{ position: 'relative', flexShrink: 0, display: 'flex', alignItems: 'center', pr: 3, ...sx }}>
        {children}
        {onResizeStart && <Box
            onMouseDown={onResizeStart}
            onTouchStart={onResizeStart}
            onDoubleClick={onResetSize}
            sx={{
                position: 'absolute',
                top: 0,
                bottom: 0,
                right: 0,
                width: 14,
                cursor: 'col-resize',
                zIndex: 1,
                '&::after': {
                    content: '""',
                    position: 'absolute',
                    top: 4,
                    bottom: 6,
                    right: 6,
                    width: 2,
                    height: 10,
                    borderRadius: 1,
                    backgroundColor: 'divider',
                },
                '&:hover::after, &:active::after': {
                    backgroundColor: 'primary.main',
                },
            }}
        />}
    </Box>
);
